import {
  type ClientProfile,
  type GuardianContact,
  type MpocFundingRequestTask,
  myTasksQueryKey,
  type RecordJointConsentEscalationOutcomeRequest,
  type Task,
  taskByIdQueryKey,
  tasksQueryKey,
  useRecordJointConsentTaskEscalationOutcomeMutation,
  useUpdateClientSecondGuardianMutation,
} from '@cartwheel/api/v1'
import {
  Button,
  RadioCard,
  RadioCardGroup,
  SegmentedControl,
  TextInput,
  useToast,
} from '@cartwheel/components'
import { formatPhoneNumber } from '@cartwheel/string-utils'
import { useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { MedicaidMcoMappingWorkspace } from './medicaid-mco-mapping-workspace'
import { MpocFundingRequestWorkspace } from './mpoc-funding-request-workspace'
import { ReviewSessionLimitReauthorizationWorkspace } from './review-session-limit-reauthorization-workspace'
import { ScheduleAdditionalSessionsWorkspace } from './schedule-additional-sessions-workspace'
import { ScheduleIntakeWorkspace } from './schedule-intake-workspace'
import { SchedulePsychIntakeWorkspace } from './schedule-psych-intake-workspace'
import { ScheduleReassignmentWorkspace } from './schedule-reassignment-workspace'
import { taskAnalyticsContext } from './task-analytics'
import { VerifyInsuranceWorkspace } from './verify-insurance-workspace'

type EscalationOutcome = RecordJointConsentEscalationOutcomeRequest['outcome']
type CompletionMethod = 'information_received' | 'unsuccessful_attempt'

const LEFT_VOICEMAIL_OUTCOME: EscalationOutcome = 'joint_custody_left_voicemail'
const CONTACT_INFO_REFUSED_OUTCOME: EscalationOutcome = 'joint_custody_contact_info_refused'
const JOINT_CUSTODY_DOES_NOT_APPLY_OUTCOME: EscalationOutcome = 'joint_custody_does_not_apply'
const PRIMARY_GUARDIAN_SOLE_AUTHORITY_OUTCOME: EscalationOutcome =
  'joint_custody_primary_guardian_sole_authority'

const CC_ESCALATION_OUTCOMES: Array<{
  value: EscalationOutcome
  label: string
  helper: string
}> = [
  {
    value: LEFT_VOICEMAIL_OUTCOME,
    label: 'Called and left voicemail with primary guardian',
    helper:
      'The workflow will wait 2 days before sending follow-up, then another 2 days before closing.',
  },
  {
    value: CONTACT_INFO_REFUSED_OUTCOME,
    label: 'Primary guardian declined to provide secondary guardian info',
    helper: 'Referral will be closed and marked services declined by client.',
  },
  {
    value: JOINT_CUSTODY_DOES_NOT_APPLY_OUTCOME,
    label: 'Joint custody doesn’t apply',
    helper: 'Joint custody workflow will be marked resolved and client will move forward.',
  },
  {
    value: PRIMARY_GUARDIAN_SOLE_AUTHORITY_OUTCOME,
    label: 'Primary guardian has sole authority to consent',
    helper: 'Joint custody workflow will be marked resolved and client will move forward.',
  },
]

export function TaskWorkspace({
  task,
  client,
  canWork = true,
  onClose,
}: {
  task: Task
  client?: ClientProfile
  canWork?: boolean
  onClose: () => void
}) {
  switch (task.type) {
    case 'follow_up_for_secondary_contact':
      return <GuardianWorkspace task={task} client={client} canWork={canWork} onClose={onClose} />
    case 'schedule_additional_sessions':
      return <ScheduleAdditionalSessionsWorkspace task={task} canWork={canWork} onClose={onClose} />
    case 'schedule_intake':
      return <ScheduleIntakeWorkspace task={task} canWork={canWork} onClose={onClose} />
    case 'schedule_psych_intake':
      return <SchedulePsychIntakeWorkspace task={task} canWork={canWork} onClose={onClose} />
    case 'schedule_reassignment':
      return <ScheduleReassignmentWorkspace task={task} canWork={canWork} onClose={onClose} />
    case 'verify_insurance_and_send_cost_estimate':
      return <VerifyInsuranceWorkspace task={task} canWork={canWork} onClose={onClose} />
    case 'medicaid_mco_mapping':
      return <MedicaidMcoMappingWorkspace task={task} canWork={canWork} onClose={onClose} />
    case 'review_session_limit_reauthorization':
      return (
        <ReviewSessionLimitReauthorizationWorkspace
          task={task}
          canWork={canWork}
          onClose={onClose}
        />
      )
    case 'send_mpoc_funding_request_manually':
      return (
        <MpocFundingRequestWorkspace
          task={task as MpocFundingRequestTask}
          canWork={canWork}
          onClose={onClose}
        />
      )
    default:
      return <GenericWorkspace task={task} />
  }
}

function TaskDescription({ task }: { task: Task }) {
  return (
    <section>
      <h3 className="text-base font-semibold text-charcoal">Description</h3>
      <p className="mt-1 text-sm leading-[1.5] text-charcoal-50">{task.description}</p>
    </section>
  )
}

function PrimaryGuardianContactCard({ guardian }: { guardian: GuardianContact }) {
  return (
    <div className="flex h-[77px] items-center overflow-hidden rounded-lg border border-sand bg-white p-4 shadow-sm">
      <div className="flex w-full items-center justify-between gap-6">
        <div className="min-w-0">
          <p className="truncate text-base font-semibold leading-[1.5] text-charcoal">
            {guardian.name || 'Primary guardian'}
          </p>
          <p className="whitespace-nowrap text-sm leading-[1.5] text-charcoal-50">
            Primary Guardian
          </p>
        </div>
        <span className="shrink-0 whitespace-nowrap text-sm font-semibold leading-[1.5] text-forest">
          {formatPhoneNumber(guardian.phone_number)}
        </span>
      </div>
    </div>
  )
}

function SectionDivider() {
  return <hr className="border-gray-stroke" />
}

function GuardianWorkspace({
  task,
  client,
  canWork,
  onClose,
}: {
  task: Task
  client?: ClientProfile
  canWork: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [completionMethod, setCompletionMethod] = useState<CompletionMethod>('information_received')
  const [selectedOutcome, setSelectedOutcome] = useState<EscalationOutcome>(LEFT_VOICEMAIL_OUTCOME)
  const isJointConsentEscalationTask = task.type === 'follow_up_for_secondary_contact'

  const handleCompletionSuccess = (description: string) => {
    void queryClient.invalidateQueries({ queryKey: myTasksQueryKey() })
    void queryClient.invalidateQueries({ queryKey: tasksQueryKey() })
    void queryClient.invalidateQueries({
      queryKey: taskByIdQueryKey({ path: { id: task.id } }),
    })
    toast.success({ description })
    onClose()
  }

  const mutation = useUpdateClientSecondGuardianMutation({
    onSuccess: () => {
      handleCompletionSuccess('Guardian information saved')
    },
    onError: () => {
      toast.error({ description: 'Failed to save guardian information' })
    },
  })
  const escalationOutcomeMutation = useRecordJointConsentTaskEscalationOutcomeMutation({
    onSuccess: () => {
      handleCompletionSuccess('Task marked complete')
    },
    onError: () => {
      toast.error({ description: 'Failed to complete task' })
    },
  })

  const canSubmit = firstName.trim() && lastName.trim() && email.trim() && phoneNumber.trim()

  const handleComplete = () => {
    mutation.mutate({
      path: { client_id: task.resource_id },
      body: {
        second_guardian_information: {
          task_id: task.id,
          first_name: firstName,
          last_name: lastName,
          email,
          phone_number: phoneNumber,
        },
      },
    })
  }

  const handleEscalationOutcome = () => {
    escalationOutcomeMutation.mutate({
      path: { task_id: task.id },
      body: {
        outcome: selectedOutcome,
      },
    })
  }

  if (task.status === 'completed') {
    return (
      <div className="space-y-8">
        <TaskDescription task={task} />
        {client?.primary_guardian && (
          <>
            <PrimaryGuardianContactCard guardian={client.primary_guardian} />
            <SectionDivider />
          </>
        )}
        <CompletionSummary task={task} client={client} />
      </div>
    )
  }

  if (task.status === 'canceled') {
    return (
      <div className="space-y-8">
        <TaskDescription task={task} />
        {client?.primary_guardian && (
          <>
            <PrimaryGuardianContactCard guardian={client.primary_guardian} />
            <SectionDivider />
          </>
        )}
        <CanceledNotice />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <TaskDescription task={task} />
      {client?.primary_guardian && (
        <>
          <PrimaryGuardianContactCard guardian={client.primary_guardian} />
          <SectionDivider />
        </>
      )}

      {canWork && (
        <>
          <section>
            <h3 className="text-base font-semibold leading-[1.5] text-charcoal">Task Completion</h3>
            <p className="mt-1 text-sm leading-[1.5] text-charcoal-50">
              Add below the information you receive or log an attempt.
            </p>
          </section>

          <CompletionMethodControl
            task={task}
            canSelectJointCustodyOutcome={isJointConsentEscalationTask}
            value={completionMethod}
            onChange={setCompletionMethod}
          />

          {completionMethod === 'information_received' ? (
            <>
              <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                <FieldInput label="First name" value={firstName} onChange={setFirstName} />
                <FieldInput label="Last name" value={lastName} onChange={setLastName} />
                <FieldInput label="Phone" value={phoneNumber} onChange={setPhoneNumber} />
                <FieldInput label="Email" value={email} onChange={setEmail} />
              </div>

              <Button
                className="w-[221px]"
                disabled={!canSubmit || mutation.isPending}
                onClick={handleComplete}
              >
                {mutation.isPending ? 'Saving…' : 'Save and mark complete'}
              </Button>
            </>
          ) : (
            <>
              <OutcomeRadioGroup
                task={task}
                value={selectedOutcome}
                onChange={setSelectedOutcome}
              />
              <Button
                className="w-[221px]"
                disabled={escalationOutcomeMutation.isPending}
                onClick={handleEscalationOutcome}
              >
                {escalationOutcomeMutation.isPending ? 'Saving…' : 'Save and mark complete'}
              </Button>
            </>
          )}
        </>
      )}
    </div>
  )
}

function CanceledNotice() {
  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold leading-[1.5] text-charcoal">Task Canceled</h3>
      <div className="rounded-lg border border-sand bg-white p-4 shadow-sm">
        <p className="text-sm leading-[1.5] text-charcoal-50">
          This task was canceled and did not require completion.
        </p>
      </div>
    </section>
  )
}

function isEscalationOutcome(value: unknown): value is EscalationOutcome {
  return CC_ESCALATION_OUTCOMES.some((o) => o.value === value)
}

function CompletionSummary({ task, client }: { task: Task; client?: ClientProfile }) {
  const rawOutcome = task.metadata?.['outcome']
  const outcome = isEscalationOutcome(rawOutcome) ? rawOutcome : null

  if (outcome) {
    const outcomeInfo = CC_ESCALATION_OUTCOMES.find((o) => o.value === outcome)
    return (
      <section className="space-y-3">
        <h3 className="text-base font-semibold leading-[1.5] text-charcoal">Outcome</h3>
        <div className="rounded-lg border border-sand bg-white p-4 shadow-sm">
          <p className="text-sm font-semibold leading-[1.5] text-charcoal">{outcomeInfo?.label}</p>
          {outcomeInfo?.helper && (
            <p className="mt-1 text-sm leading-[1.5] text-charcoal-50">{outcomeInfo.helper}</p>
          )}
        </div>
      </section>
    )
  }

  const secondGuardian = client?.second_guardian
  if (secondGuardian) {
    return (
      <section className="space-y-3">
        <h3 className="text-base font-semibold leading-[1.5] text-charcoal">
          Secondary Guardian Added
        </h3>
        <div className="flex h-[77px] items-center overflow-hidden rounded-lg border border-sand bg-white p-4 shadow-sm">
          <div className="flex w-full items-center justify-between gap-6">
            <div className="min-w-0">
              <p className="truncate text-base font-semibold leading-[1.5] text-charcoal">
                {secondGuardian.name || 'Secondary guardian'}
              </p>
              <p className="whitespace-nowrap text-sm leading-[1.5] text-charcoal-50">
                {secondGuardian.email}
              </p>
            </div>
            <span className="shrink-0 whitespace-nowrap text-sm font-semibold leading-[1.5] text-forest">
              {formatPhoneNumber(secondGuardian.phone_number)}
            </span>
          </div>
        </div>
      </section>
    )
  }

  return null
}

function CompletionMethodControl({
  task,
  canSelectJointCustodyOutcome,
  value,
  onChange,
}: {
  task: Task
  canSelectJointCustodyOutcome: boolean
  value: CompletionMethod
  onChange: (value: CompletionMethod) => void
}) {
  return (
    <SegmentedControl.Root
      value={value}
      onChange={canSelectJointCustodyOutcome ? onChange : undefined}
      track={{
        name: 'TASK_RESOLUTION_METHOD_SELECTED',
        properties: ({ value: resolutionMethod }) => ({
          ...taskAnalyticsContext(task),
          resolutionMethod,
        }),
      }}
    >
      <SegmentedControl.Option
        value="information_received"
        disabled={!canSelectJointCustodyOutcome}
      >
        Information Received
      </SegmentedControl.Option>
      <SegmentedControl.Option
        value="unsuccessful_attempt"
        disabled={!canSelectJointCustodyOutcome}
      >
        Unsuccessful Attempt
      </SegmentedControl.Option>
    </SegmentedControl.Root>
  )
}

function OutcomeRadioGroup({
  task,
  value,
  onChange,
}: {
  task: Task
  value: EscalationOutcome
  onChange: (value: EscalationOutcome) => void
}) {
  return (
    <RadioCardGroup
      name="cc-escalation-outcome"
      value={value}
      onValueChange={(selectedValue) => onChange(selectedValue as EscalationOutcome)}
      className="space-y-2"
      track={{
        name: 'TASK_RESOLUTION_OUTCOME_SELECTED',
        properties: ({ value: resolutionOutcome }) => ({
          ...taskAnalyticsContext(task),
          resolutionOutcome,
        }),
      }}
    >
      {CC_ESCALATION_OUTCOMES.map((outcome) => (
        <RadioCard
          key={outcome.value}
          value={outcome.value}
          title={outcome.label}
          subtitle={outcome.helper}
        />
      ))}
    </RadioCardGroup>
  )
}

function FieldInput({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  const inputId = useId()

  return (
    <div className="grid h-[72px] gap-2">
      <label className="text-base font-semibold leading-[1.5] text-charcoal" htmlFor={inputId}>
        {label}
      </label>
      <TextInput id={inputId} value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  )
}

function GenericWorkspace({ task }: { task: Task }) {
  return (
    <div className="space-y-8">
      <TaskDescription task={task} />
    </div>
  )
}
